import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  // null = vérification en cours, false = non connecté, objet = connecté
  const [user, setUser] = useState(null);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("authToken");
    if (!token) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("authToken");
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Capture d'un code de parrainage éventuel (?ref=CODE) pour réclamation après connexion
    try {
      const p = new URLSearchParams(window.location.search);
      const r = p.get("ref");
      if (r) localStorage.setItem("pendingRef", r.trim().toUpperCase());
    } catch { /* noop */ }
    // Si on revient d'un callback Google (session_id dans le hash), on laisse AuthCallback gérer.
    if (window.location.hash?.includes("session_id=")) return;
    loadMe();
  }, [loadMe]);

  // Réclame le parrainage une fois l'utilisateur connecté (s'il n'a pas déjà de parrain)
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    const pending = localStorage.getItem("pendingRef");
    if (!pending) return;
    if (user.referred_by || pending === user.referral_code) {
      localStorage.removeItem("pendingRef");
      return;
    }
    api.post("/prefs/referral-claim", { code: pending })
      .then(() => { localStorage.removeItem("pendingRef"); loadMe(); })
      .catch(() => { localStorage.removeItem("pendingRef"); });
  }, [user, loadMe]);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    localStorage.setItem("authToken", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("authToken", data.token);
    setUser(data.user);
    return data.user;
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* noop */ }
    localStorage.removeItem("authToken");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, googleLogin, logout, refreshUser: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}
